import React, { useEffect, useState } from 'react';
import '../styles/GPTWrapper.css';
import { Header, Footer, Input, Button, Radio } from '../components';
import { checkOnAuthStateChanged, cloudFunctionV2 } from '../functions';
import { useNavigate } from 'react-router-dom';

function GPTWrapper() {
    const [loggedIn, setLoggedIn] = useState(null);
    const [systemPrompt, setSystemPrompt] = useState("");
    const [prompt, setPrompt] = useState("");
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState(null);
    const [service, setService] = useState("gemini");
    const [model, setModel] = useState("gemini-2.5-flash-preview-05-20");
    const [error, setError] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [textError, setTextError] = useState(null);
    const [extraData, setExtraData] = useState("");
    // const [relevantObject] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        let interval;

        if (loading) {
            interval = setInterval(() => {
                setElapsedTime(prevTime => Math.round((prevTime + 0.1) * 10) / 10); // Increment by 0.1s
            }, 100);
        } else {
            setElapsedTime(0);
        }

        return () => clearInterval(interval); // Cleanup on unmount or when loading stops
    }, [loading]);

    useEffect(() => {
        document.title = "GPT Wrapper | Clozers";

        const fetchUserData = async () => {
            const user = await checkOnAuthStateChanged();
            if (user) {
                const adminIds = JSON.parse(process.env.REACT_APP_ADMINIDS);
                if (adminIds.includes(user.userId)) {
                    setLoggedIn(true);
                }
                else {
                    setLoggedIn(false);
                    navigate("/");
                }
            }
            else {
                setLoggedIn(false);
                navigate("/");
            }
        };

        fetchUserData();
    }, [navigate]);

    const options = [
        {
            label: "ChatGPT",
            value: "openai",
            models: [
                {
                    label: "GPT 4o Mini",
                    value: "gpt-4o-mini"
                },
                {
                    label: "GPT 4o",
                    value: "gpt-4o"
                },
                {
                    label: "O3 Mini",
                    value: "o3-mini"
                },
                {
                    label: "O3",
                    value: "o3"
                }
            ]
        },
        {
            label: "Gemini",
            value: "gemini",
            models: [
                {
                    label: "Gemini 2.5 Flash Preview 05/20",
                    value: "gemini-2.5-flash-preview-05-20"
                },
                {
                    label: "Gemini 2.5 Pro Preview 06/05",
                    value: "gemini-2.5-pro-preview-06-05"
                }
            ]
        },
        {
            label: "DeepSeek",
            value: "deepseek",
            models: [
                {
                    label: "DeepSeek Chat",
                    value: "deepseek-chat"
                },
                {
                    label: "DeepSeek R1",
                    value: "deepseek-reasoner"
                }
            ]
        },
        {
            label: "Grok",
            value: "grok",
            models: [
                {
                    label: "Grok 2",
                    value: "grok-2-1212"
                }
            ]
        }
    ];

    const providerIndex = options.findIndex(item => item.value === service);

    const queryGPT = async() => {
        if ( model === "" ) {
            setTextError("Please select a model");
            return;
        }
        else if ( prompt === "" ) {
            setTextError("Please enter a prompt");
            return;
        }
        // else if ( relevantObject === null ) {
        //     setTextError("Please select a property");
        //     return;
        // }

        setTextError(null);
        setLoading(true);
        setResponse(null);
        setError(null);
        setElapsedTime(0);
        const url = process.env.REACT_APP_QUERY_GPT;

        const JSONPrompt = extraData === "" ? "" : `\`\`\`json\n${extraData}\n\`\`\``;
        const prompText = extraData === "" ? prompt : `${prompt}\n\n${JSONPrompt}`;

        const params = {
            service: service,
            model: model,
            prompt: prompText,
            systemInstructions: systemPrompt === "" ? null : systemPrompt,
            // Enable Google Search grounding for Gemini calls
            // Set to `true` by default; adjust dynamically if you add a UI toggle
            googleSearchRetrieval: false,
            // Optional: fine‑tune dynamic retrieval (Gemini‑1.5 only)
            // dynamicRetrievalConfig: { mode: "MODE_DYNAMIC", dynamicThreshold: 0.4 }
        };
        const queryData = await cloudFunctionV2(url, params);
        console.log(queryData)
        if ( queryData.status === 200 ) {
            const data = queryData.data;
            setResponse(data);
        }
        else {
            console.log(queryData.error)
            if ( queryData.error.statusText !== undefined ) {
                setError(queryData.error.statusText);
            }
            else {
                setError(queryData.error.error.message);
            }
        }
        setLoading(false);
    };

    const onChangeSystemPrompt = (e) => {
        setSystemPrompt(e.target.value);
    };

    const onChangePrompt = (e) => {
        setPrompt(e.target.value);
    };

    const onChangeService = (value) => {
        setService(value);
        setModel("");
    };

    const onChangeModel = (value) => {
        setModel(value);
    };

    const onChangeExtraData = (e) => {
        setExtraData(e.target.value);
    };

    return (
        <div className="gpt-wrapper-outer-container">
            <Header
                loggedIn={loggedIn}
            />
            {
                loggedIn === true ?
                <div className="gpt-wrapper-inner-container">
                    <div className="gpt-wrapper-left-container">
                        <div className="gpt-wrapper-options-container">
                            <span className="body-bold colour-primary">
                                Provider
                            </span>
                            {
                                options.map((item, index) =>
                                    <Radio
                                        label={item.label}
                                        type="regular"
                                        selected={item.value === service ? true : false}
                                        key={index}
                                        identifier={item.value}
                                        selectRadio={(identifier) => onChangeService(identifier)}
                                    />
                                )
                            }
                        </div>
                        <div className="gpt-wrapper-options-container">
                            <span className="body-bold colour-primary">
                                Model
                            </span>
                            {
                                options[providerIndex].models.map((item, index) =>
                                    <Radio
                                        label={item.label}
                                        type="regular"
                                        selected={item.value === model ? true : false}
                                        key={index}
                                        identifier={item.value}
                                        selectRadio={(identifier) => onChangeModel(identifier)}
                                    />
                                )
                            }
                        </div>
                        <div className="gpt-wrapper-input-container">
                            <Input
                                label={"System prompt"}
                                labelType={"default"}
                                secondaryLabelGrey={null}
                                info={false}
                                value={systemPrompt}
                                onChange={onChangeSystemPrompt}
                                placeholder={"Enter a system prompt"}
                                type="text"
                                leftUnit={null}
                                rightUnit={null}
                                unitSwitch={false}
                                leftUnitSelected={true}
                                unitSwitchFunc={null}
                                disabled={false}
                                multiline={true}
                                error={false}
                                onMouseEnter={null}
                                onMouseLeave={null}
                                reset={false}
                                resetFunc={null}
                                resetPosition={null}
                                focusMultiline={false}
                                prompt={false}
                            />
                        </div>
                        <div className="gpt-wrapper-input-container">
                            <Input
                                label={"User prompt"}
                                labelType={"default"}
                                secondaryLabelGrey={null}
                                info={false}
                                value={prompt}
                                onChange={onChangePrompt}
                                placeholder={"Enter a prompt"}
                                type="text"
                                leftUnit={null}
                                rightUnit={null}
                                unitSwitch={false}
                                leftUnitSelected={true}
                                unitSwitchFunc={null}
                                disabled={false}
                                multiline={true}
                                error={false}
                                onMouseEnter={null}
                                onMouseLeave={null}
                                reset={false}
                                resetFunc={null}
                                resetPosition={null}
                                focusMultiline={false}
                                prompt={false}
                            />
                        </div>
                        <div className="gpt-wrapper-input-container">
                            <Input
                                label={"JSON data"}
                                labelType={"default"}
                                secondaryLabelGrey={null}
                                info={false}
                                value={extraData}
                                onChange={onChangeExtraData}
                                placeholder={"Enter any JSON data here"}
                                type="text"
                                leftUnit={null}
                                rightUnit={null}
                                unitSwitch={false}
                                leftUnitSelected={true}
                                unitSwitchFunc={null}
                                disabled={false}
                                multiline={false}
                                error={false}
                                onMouseEnter={null}
                                onMouseLeave={null}
                                reset={false}
                                resetFunc={null}
                                resetPosition={null}
                                focusMultiline={false}
                                prompt={false}
                            />
                        </div>
                        {
                            textError !== null &&
                            <span className="body-regular colour-error">
                                {textError}
                            </span>
                        }
                        <div className="gpt-wrapper-button-container">
                            <Button
                                buttonType="primary-button"
                                size="large"
                                text={"Run"}
                                cta={() => queryGPT()}
                                disabled={loading}
                                leftIcon={null}
                                leftIconClass=""
                                rightIcon={null}
                                rightIconClass=""
                                alt=""
                                standardIcon={false}
                                badge={false}
                                badgeVal={null}
                                badgeBG={null}
                                buttonRef={null}
                                spinner={false}
                            />
                        </div>
                    </div>
                    <div className="gpt-wrapper-right-container">
                        {
                            loading === true ?
                            <div className="gpt-wrapper-loading-container">
                                <span className="body-bold colour-primary">
                                    {elapsedTime} seconds
                                </span>
                                <span className="body-regular colour-primary">
                                    Loading...
                                </span>
                            </div>
                            :
                            error !== null ?
                            <span className="body-regular colour-error">
                                {error}
                            </span>
                            :
                            response !== null ?
                            <span className="body-regular colour-primary block-text white-space-pre-wrap">
                                {response}
                            </span>
                            :
                            <span className="body-regular colour-primary">
                                Your response will appear here
                            </span>
                        }
                    </div>
                </div>
                :
                <div className="gpt-wrapper-inner-container">
                </div>
            }
            <Footer />
        </div>
    )
};

export default GPTWrapper;